import React, { useCallback, useEffect, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { CloseOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { IconButton, Input, LinkButton, Popover, Stack, Typography } from '@lyearn/ui';

import { FEEDBACK_REACTIONS } from '../../consts';
import { renderButton } from './renderers';
import { inputStyles } from './styles';
import { FeedbackActionsProps } from './types';

const FEEDBACK_REACTIONS_VALUE = Object.values(FEEDBACK_REACTIONS);

const FeedbackActions: React.FC<FeedbackActionsProps> = (props) => {
  const {
    onSubmit,
    disabled,
    onIconClick,
    dialogOpen,
    onClose,
    emojiResponse,
    position = 'bottom-end',
    buttonSize,
  } = props;
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const [ref, setRef] = useState<HTMLInputElement | null>(null);
  const [userMessage, setUserMessage] = useState('');

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  const onSend = useCallback(() => {
    onSubmit(userMessage);
    onClose();
  }, [onClose, onSubmit, userMessage]);

  useEffect(() => {
    ref?.focus();
  }, [ref]);

  return (
    <>
      {FEEDBACK_REACTIONS_VALUE.map((reaction) => {
        if (reaction.showPopover) {
          return (
            <Popover key={reaction.id} open={dialogOpen} onOpenChange={onOpenChange}>
              <Popover.Button key={reaction.id}>
                {renderButton({
                  t,
                  reaction,
                  onClick: onIconClick,
                  disabled,
                  emojiResponse,
                  buttonSize,
                })}
              </Popover.Button>
              <Popover.Content position={position} renderAs={isMobile ? 'bottom-sheet' : 'popover'}>
                <Stack alignItems="end" className="p-16" direction="col" gapY={4}>
                  <Stack>
                    <Input
                      autoFocus
                      multiline
                      className={cn('body-short-02 w-[21.2rem] border-none', inputStyles)}
                      inputRef={setRef}
                      maxRows={12}
                      minRows={5}
                      placeholder={t('Common:AIFeedback.InputPlaceholder')}
                      value={userMessage}
                      onChangeText={setUserMessage}
                    />
                    <IconButton
                      Icon={CloseOutline}
                      appearance="secondary"
                      aria-label={t('Common:Actions.Close')}
                      className="-mt-8 -me-8"
                      disabled={disabled}
                      size="s"
                      variant="ghost"
                      onClick={onClose}
                    />
                  </Stack>
                  <LinkButton className="flex-shrink-0 me-4" onClick={onSend}>
                    <Typography
                      color={userMessage ? 'text-text-primary' : 'text-text-placeholder'}
                      variant="heading-xxs">
                      {t('Common:AIFeedback.Send')}
                    </Typography>
                  </LinkButton>
                </Stack>
              </Popover.Content>
            </Popover>
          );
        } else {
          return (
            <>
              {renderButton({
                t,
                reaction,
                onClick: onIconClick,
                disabled,
                emojiResponse,
                buttonSize,
              })}
            </>
          );
        }
      })}
    </>
  );
};

export default FeedbackActions;
